import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, X, Star, Video, Loader2, Upload } from "lucide-react";

const empty = {
  name: "", country: "", city: "", description: "", hero_image: "", gallery: [], certifications: [],
  languages: ["English", "Spanish"], rating: 4.8, reviews_count: 0, years_experience: 10,
  packages: [], procedures: [], before_after: [], videos: [], map_embed: "", address: "", featured: false,
};

function CsvField({ label, value, onChange, placeholder }) {
  return (
    <div className="space-y-2">
      <Label>{label} <span className="text-muted-foreground text-xs">(comma separated)</span></Label>
      <Input value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder={placeholder} />
    </div>
  );
}

export default function ClinicsAdmin() {
  const [clinics, setClinics] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/clinics").then((r) => setClinics(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  const setProc = (i, k, v) => setForm((f) => {
    const procedures = [...f.procedures];
    procedures[i] = { ...procedures[i], [k]: k === "name" ? v : parseFloat(v) || 0 };
    return { ...f, procedures };
  });
  const addProc = () => set("procedures", [...form.procedures, { name: "", price: 0, usa_price: 0 }]);
  const removeProc = (i) => set("procedures", form.procedures.filter((_, idx) => idx !== i));

  const setBa = (i, k, v) => setForm((f) => {
    const before_after = [...(f.before_after || [])];
    before_after[i] = { ...before_after[i], [k]: v };
    return { ...f, before_after };
  });
  const addBa = () => set("before_after", [...(form.before_after || []), { before: "", after: "", label: "" }]);
  const removeBa = (i) => set("before_after", (form.before_after || []).filter((_, idx) => idx !== i));

  const videoInputRef = useRef(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const setVideoTitle = (i, v) => setForm((f) => {
    const videos = [...(f.videos || [])];
    videos[i] = { ...videos[i], title: v };
    return { ...f, videos };
  });
  const removeVideo = (i) => set("videos", (form.videos || []).filter((_, idx) => idx !== i));
  const uploadVideo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setVideoUploading(true);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.url.startsWith("http") ? res.data.url : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      const title = file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ");
      setForm((f) => ({ ...f, videos: [...(f.videos || []), { url, title }] }));
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setVideoUploading(false);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name || !form.country || !form.city) { toast.error("Name, country and city are required"); return; }
    const payload = { ...form, rating: parseFloat(form.rating) || 0, reviews_count: parseInt(form.reviews_count) || 0, years_experience: parseInt(form.years_experience) || 0 };
    try {
      if (editing) { await api.put(`/admin/clinics/${editing.id}`, payload); toast.success("Clinic updated"); }
      else { await api.post("/admin/clinics", payload); toast.success("Clinic created"); }
      setOpen(false); load();
    } catch (e) { toast.error("Failed to save clinic"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this clinic?")) return;
    await api.delete(`/admin/clinics/${id}`);
    toast.success("Clinic deleted"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Clinics</h1>
          <p className="text-muted-foreground mt-1">Add, edit and manage clinics, prices and photos.</p>
        </div>
        <Button className="rounded-full" onClick={openNew} data-testid="add-clinic-btn"><Plus className="w-4 h-4 mr-2" /> Add clinic</Button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 mt-6">
        {clinics.map((c) => (
          <Card key={c.id} className="rounded-2xl overflow-hidden" data-testid={`admin-clinic-${c.id}`}>
            <div className="h-32 overflow-hidden relative">
              <img src={c.hero_image} alt={c.name} className="w-full h-full object-cover" />
              {c.featured && <span className="absolute top-2 left-2 text-xs bg-accent text-white rounded-full px-2 py-0.5 flex items-center gap-1"><Star className="w-3 h-3" /> Featured</span>}
            </div>
            <div className="p-4">
              <h3 className="font-heading font-bold">{c.name}</h3>
              <p className="text-sm text-muted-foreground">{c.city}, {c.country}</p>
              <p className="text-xs text-muted-foreground mt-1">{c.procedures?.length || 0} procedures</p>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" className="rounded-full flex-1" onClick={() => openEdit(c)} data-testid={`edit-clinic-${c.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`delete-clinic-${c.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="clinic-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit clinic" : "Add clinic"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="clinic-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Country *</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="clinic-country-input" /></div>
                <div className="space-y-2"><Label>City *</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="clinic-city-input" /></div>
              </div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="clinic-description-input" /></div>
            <div className="space-y-2"><Label>Hero image URL</Label><Input value={form.hero_image} onChange={(e) => set("hero_image", e.target.value)} placeholder="https://…" data-testid="clinic-hero-input" /></div>
            <CsvField label="Gallery image URLs" value={form.gallery} onChange={(v) => set("gallery", v)} placeholder="https://…, https://…" />
            <CsvField label="Certifications" value={form.certifications} onChange={(v) => set("certifications", v)} placeholder="JCI Accredited, ISO 9001" />
            <CsvField label="Languages" value={form.languages} onChange={(v) => set("languages", v)} placeholder="English, Spanish" />
            <CsvField label="Packages / included" value={form.packages} onChange={(v) => set("packages", v)} placeholder="Airport pickup, Hotel rates" />
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label>Rating</Label><Input type="number" step="0.1" value={form.rating} onChange={(e) => set("rating", e.target.value)} /></div>
              <div className="space-y-2"><Label>Reviews</Label><Input type="number" value={form.reviews_count} onChange={(e) => set("reviews_count", e.target.value)} /></div>
              <div className="space-y-2"><Label>Years exp.</Label><Input type="number" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} /></div>
            </div>
            <div className="space-y-2"><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div className="space-y-2"><Label>Map embed URL</Label><Input value={form.map_embed} onChange={(e) => set("map_embed", e.target.value)} placeholder="https://www.google.com/maps?q=…&output=embed" /></div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Procedures & prices</Label>
                <Button variant="outline" size="sm" className="rounded-full" onClick={addProc} data-testid="add-procedure-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
              </div>
              <div className="space-y-2">
                {form.procedures.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-6" placeholder="Procedure name" value={p.name} onChange={(e) => setProc(i, "name", e.target.value)} data-testid={`proc-name-${i}`} />
                    <Input className="col-span-3" type="number" placeholder="Price" value={p.price} onChange={(e) => setProc(i, "price", e.target.value)} data-testid={`proc-price-${i}`} />
                    <Input className="col-span-2" type="number" placeholder="USA" value={p.usa_price} onChange={(e) => setProc(i, "usa_price", e.target.value)} data-testid={`proc-usa-${i}`} />
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeProc(i)}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Before &amp; after (smile transformations)</Label>
                <Button variant="outline" size="sm" className="rounded-full" onClick={addBa} data-testid="add-ba-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
              </div>
              <div className="space-y-2">
                {(form.before_after || []).map((b, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-4" placeholder="Before image URL" value={b.before} onChange={(e) => setBa(i, "before", e.target.value)} data-testid={`ba-before-${i}`} />
                    <Input className="col-span-4" placeholder="After image URL" value={b.after} onChange={(e) => setBa(i, "after", e.target.value)} data-testid={`ba-after-${i}`} />
                    <Input className="col-span-3" placeholder="Label" value={b.label} onChange={(e) => setBa(i, "label", e.target.value)} data-testid={`ba-label-${i}`} />
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeBa(i)}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Patient videos <span className="text-muted-foreground text-xs">(upload from device)</span></Label>
                <Button variant="outline" size="sm" className="rounded-full" disabled={videoUploading} onClick={() => videoInputRef.current?.click()} data-testid="add-video-btn">
                  {videoUploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Upload video
                </Button>
                <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} />
              </div>
              <div className="space-y-2">
                {(form.videos || []).map((v, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`clinic-video-${i}`}>
                    <video src={v.url} className="w-24 h-16 object-cover rounded-lg bg-black shrink-0" />
                    <Input placeholder="Video title" value={v.title || ""} onChange={(e) => setVideoTitle(i, e.target.value)} data-testid={`clinic-video-title-${i}`} />
                    <Button variant="ghost" size="icon" onClick={() => removeVideo(i)} data-testid={`remove-video-${i}`}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                {(form.videos || []).length === 0 && <p className="text-xs text-muted-foreground flex items-center gap-1"><Video className="w-3.5 h-3.5" /> No videos yet — upload MP4/MOV clips to show on this clinic's page.</p>}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><Label>Featured clinic</Label><p className="text-xs text-muted-foreground">Show on homepage</p></div>
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="clinic-featured-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-full" onClick={save} data-testid="save-clinic-btn">{editing ? "Update" : "Create"} clinic</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
